import { InterviewsStepResponse } from "../../../api/AnalysisStep";
import { CaseNote } from "../../../api/CaseNote";
import { Interview } from "../../../api/Interviews";
import { fetchInterviewsStep } from "../../../thunks/AnalysisStep";
import { createEntityAdapter, createSlice, EntityState, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { createInterview, updateInterview } from "../../../thunks/Interviews";


export interface InterviewWithState {
    id: string
    interview: Interview
    status: 'idle' | 'editing' | 'saving' | 'error'
}

export type CreateInterviewStatus = 'idle' | 'editing' | 'saving' | 'error'

export interface InterviewAnalysisState extends EntityState<InterviewWithState, string> {
    status: 'idle' | 'loading' | 'error'
    remarks?: CaseNote
    createInterviewStatus: CreateInterviewStatus
}

const interviewsAdapter = createEntityAdapter<InterviewWithState>({})

const initialState : InterviewAnalysisState = interviewsAdapter.getInitialState({
    status: 'idle',
    createInterviewStatus: 'idle'    
})

const interviewAnalysisSlice = createSlice({
    name: 'interviewAnalysis',
    initialState,
    reducers: {
        setCreateInterviewStatus(
            state: InterviewAnalysisState,
            action: PayloadAction<CreateInterviewStatus>
        ){
            state.createInterviewStatus = action.payload
        },
        addTemporaryInterview(
            state: InterviewAnalysisState,
            action: PayloadAction<InterviewWithState>
        ){
            interviewsAdapter.addOne(state, action.payload)
        },
        removeTemporaryInterview(
            state: InterviewAnalysisState,
            action: PayloadAction<string>
        ){
            interviewsAdapter.removeOne(state, action.payload)
        },
        editInterview(
            state: InterviewAnalysisState,
            action: PayloadAction<string>
        ){
            state.entities[action.payload].status = 'editing'
        },
        cancelEditInterview(
            state: InterviewAnalysisState,
            action: PayloadAction<string>
        ){
            state.entities[action.payload].status = 'idle'
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchInterviewsStep.pending, (state) => {
            state.status = 'loading'
        })
        builder.addCase(fetchInterviewsStep.fulfilled, (state, action: PayloadAction<InterviewsStepResponse>) => {
            state.status = 'idle';
            interviewsAdapter.setAll(
                state,
                action.payload.interview_details.map((interview) => ({id:interview.id || "",interview: interview, status: 'idle'}))
            )
            state.remarks = action.payload.interview_findings;
        })
        builder.addCase(fetchInterviewsStep.rejected, (state) => {
            state.status = 'error'
        })
        builder.addCase(createInterview.pending, (state) => {
            state.createInterviewStatus = 'saving'
        })
        builder.addCase(createInterview.fulfilled, (state, action: PayloadAction<Interview[]>) => {
            interviewsAdapter.addMany(
                state,
                action.payload.map((interview): InterviewWithState=>({id:interview.id || "",  interview: interview, status: 'idle'}))
            )
            state.createInterviewStatus = 'idle'
        })
        builder.addCase(createInterview.rejected, (state) => {
            state.createInterviewStatus = 'error'
        })
        builder.addCase(updateInterview.pending, (state, action) => {
            state.entities[action.meta.arg.id].status = 'saving'
        })
        builder.addCase(updateInterview.fulfilled, (state, action) => {
            const id = action.meta.arg.id;
            const updateRequest = action.meta.arg.request;
            const entity = state.entities[id];            
            entity.interview = {
                ...entity.interview,
                ...updateRequest
            }
            entity.status = 'idle'
        })

        builder.addCase(updateInterview.rejected, (state, action) => {
            state.entities[action.meta.arg.id].status = 'error'
        })
    }
    
})

export const {
    selectAll: getInterviews
} = interviewsAdapter.getSelectors((state: RootState) => state.interviewAnalysis)

export const getInterviewFindingsData = createSelector(
  [getInterviews],
  (interviews) => {
    return interviews.map(interviewEntity => ({
      interviewee_type: interviewEntity.interview.interviewee_type,
      findings: interviewEntity.interview.findings,
    }));
  }
);


export const getInterviewAnalysisStatus = (state: RootState) => state.interviewAnalysis.status
export const getCreateInterviewStatus = (state: RootState) => (state.interviewAnalysis.createInterviewStatus)
export const getInterviewFindingsRemarks = (state: RootState) => (state.interviewAnalysis.remarks)
// Re: `getInterviewFindingsRemarks`, see https://github.com/Yugen-ai/canso-fraud-investigation-ui/issues/89

export const { setCreateInterviewStatus, addTemporaryInterview, removeTemporaryInterview, editInterview, cancelEditInterview } = interviewAnalysisSlice.actions;
export const interviewAnalysisReducer = interviewAnalysisSlice.reducer;